/**
 * Génère et déclenche le téléchargement d'un fichier CSV à partir des données
 * agrégées, en respectant la visibilité courante des colonnes et les filtres actifs.
 *
 * Stratégie mémoire :
 *  1. showSaveFilePicker  → écriture en streaming par tranches (0 copie globale en RAM)
 *  2. Blob(parts[])       → fallback : évite le Array.join() qui double la mémoire
 *
 * @param {Object}        csvData              - { rows: { [id]: {...} }, meta: {...} }
 * @param {Object}        columnVisibilityModel - { [field]: boolean } (field avec __ au lieu de .)
 * @param {string}        separator            - séparateur CSV (';', ',', '\\t', '|')
 * @param {Set|null}      filteredIds          - IDs des lignes visibles (null = toutes)
 * @param {string}        listSeparator        - séparateur secondaire pour les valeurs multiples (' | ' par défaut)
 * @param {string[]|null} columnOrder          - clés originales dans l'ordre de la grille (null = tri par défaut)
 * @param {Object}        columnAliases        - { [field]: string } alias des colonnes (field avec __ au lieu de .)
 */

const CHUNK_ROWS = 2_000; // lignes par tranche d'écriture

export async function exportToCsv(csvData, columnVisibilityModel, separator, filteredIds = null, listSeparator = ' | ', columnOrder = null, columnAliases = {}) {
    const sep = separator === '\\t' ? '\t' : separator;
    const listSep = listSeparator ?? ' | ';

    // Toutes les cellules sont encadrées de doubles quotes (RFC 4180).
    // Les éventuels guillemets présents dans la valeur sont d'abord doublés ("").
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;



    const isGeomKey = key => key.startsWith('geom_') || key.includes('.geom_');
    const isVisible  = key => columnVisibilityModel[key.replace(/\./g, '__')] !== false;

    let visibleKeys;
    if (columnOrder && columnOrder.length > 0) {
        // Respecter l'ordre de la grille, filtrer geom et colonnes masquées
        visibleKeys = columnOrder.filter(key => !isGeomKey(key) && isVisible(key));
    } else {
        // Tri par défaut : meta.* en tête, puis tri alphabétique
        visibleKeys = Object.keys(csvData.meta)
            .filter(key => !isGeomKey(key) && isVisible(key))
            .sort((a, b) => {
                const aMeta = a.startsWith('meta.');
                const bMeta = b.startsWith('meta.');
                if (aMeta && !bMeta) return -1;
                if (!aMeta && bMeta) return  1;
                return a.localeCompare(b, 'fr', { sensitivity: 'base' });
            });
    }

    if (visibleKeys.length === 0) return;

    // Alias : pour chaque clé, chercher l'alias via le field (__ au lieu de .)
    const getHeader = (key) => {
        const field = key.replace(/\./g, '__');
        return (columnAliases && columnAliases[field]) ? columnAliases[field] : key;
    };

    // Lignes à exporter (toutes ou seulement les filtrées)
    const rowEntries = Object.entries(csvData.rows)
        .filter(([id]) => !filteredIds || filteredIds.has(id));

    const buildLine = ([, row]) =>
        visibleKeys.map(key => {
            const v = row[key];
            return esc(Array.isArray(v) ? [...new Set(v)].join(listSep) : String(v ?? ''));
        }).join(sep);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename  = `export-${timestamp}.csv`;
    const header    = visibleKeys.map(key => esc(getHeader(key))).join(sep);
    const enc       = new TextEncoder();

    // ── 1. Streaming via File System Access API (Chrome / Edge) ──────────────
    if ('showSaveFilePicker' in window) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: filename,
                types: [{ description: 'Fichier CSV', accept: { 'text/csv': ['.csv'] } }],
            });
            const writable = await handle.createWritable();
            await writable.write(enc.encode('\ufeff' + header + '\n'));
            for (let i = 0; i < rowEntries.length; i += CHUNK_ROWS) {
                const chunk = rowEntries.slice(i, i + CHUNK_ROWS);
                await writable.write(enc.encode(chunk.map(buildLine).join('\n') + '\n'));
            }
            await writable.close();
            return;
        } catch (e) {
            if (e.name === 'AbortError') return; // annulé par l'utilisateur
            // Autre erreur → fallback ci-dessous
        }
    }

    // ── 2. Fallback : Blob multi-parts (évite la concaténation globale) ───────
    // new Blob(array) accepte un tableau de parties sans les fusionner en RAM
    const parts = ['\ufeff' + header + '\n'];
    for (let i = 0; i < rowEntries.length; i += CHUNK_ROWS) {
        const chunk = rowEntries.slice(i, i + CHUNK_ROWS);
        parts.push(chunk.map(buildLine).join('\n') + '\n');
    }

    const blob = new Blob(parts, { type: 'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Délai généreux pour que le navigateur ait le temps de lancer le téléchargement
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
